// @@TASK_LISTS_v100@@ shared to-do UI: checkbox row, assignee avatars/picker,
// date helpers. Used by Tasks (index + My assignments), TaskList, TaskDetail.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// DATE columns arrive as ISO strings ("2026-11-01T00:00:00.000Z"); read the
// calendar day from the string so Phoenix never shows the day before.
export function dayParts(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
export function todayLocal() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
export function fmtDue(s) {
  const d = dayParts(s);
  if (!d) return '';
  const t = todayLocal();
  const diff = Math.round((d - t) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric',
    ...(d.getFullYear() !== t.getFullYear() ? { year: 'numeric' } : {}) });
}
export function dueTone(s, status) {
  const d = dayParts(s);
  if (!d || status === 'done' || status === 'cancelled') return '';
  const diff = Math.round((d - todayLocal()) / 86400000);
  if (diff < 0) return 'hq-todo-due-late';
  if (diff <= 1) return 'hq-todo-due-soon';
  return '';
}
export function fmtWhen(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' at ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
export const STATUS_LABEL = { todo: 'To do', in_progress: 'In progress', done: 'Done', cancelled: 'Cancelled' };

const AV_COLORS = ['#31b44b', '#6ea8ff', '#e8c15a', '#b48cff', '#ff8f6b', '#4fd1c5', '#f57ab8', '#a3a3ff'];
function initials(name) {
  const p = String(name || '?').trim().split(/\s+/);
  return ((p[0] || '?')[0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}
function colorFor(id) {
  let h = 0; for (const c of String(id || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}
export function Avatar({ person, size = 22 }) {
  return (
    <span className="hq-todo-av" title={person.name} style={{ width: size, height: size, fontSize: size * 0.42, background: colorFor(person.id) }}>
      {initials(person.name)}
    </span>
  );
}

// Multi-assignee picker: chips + an "add" select of the remaining people.
export function AssigneePicker({ people, value, onChange, compact }) {
  const chosen = value.map((id) => people.find((p) => p.id === id)).filter(Boolean);
  const rest = people.filter((p) => !value.includes(p.id));
  return (
    <div className={'hq-todo-picker' + (compact ? ' hq-todo-picker-compact' : '')}>
      {chosen.map((p) => (
        <span key={p.id} className="hq-todo-chip">
          <Avatar person={p} size={18} />{p.name}
          <button type="button" aria-label={'Remove ' + p.name} onClick={() => onChange(value.filter((x) => x !== p.id))}>×</button>
        </span>
      ))}
      {rest.length > 0 && (
        <select className="hq-select hq-select-sm" value="" aria-label="Add assignee"
          onChange={(e) => { if (e.target.value) onChange([...value, e.target.value]); }}>
          <option value="">{chosen.length ? '+ Add' : 'Assign to…'}</option>
          {rest.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
    </div>
  );
}

// Round checkbox that crosses off. `checked` drives the look; parent handles the API.
export function CheckCircle({ checked, onClick, label }) {
  return (
    <button type="button" className={'hq-todo-check' + (checked ? ' is-checked' : '')}
      aria-pressed={checked} aria-label={label || (checked ? 'Mark not done' : 'Mark done')}
      onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" /></svg>
    </button>
  );
}

// PATCH status; returns the server response JSON (or null).
export async function setDone(task, done) {
  const res = await apiFetch(`/api/tasks/${task.id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: done ? 'done' : 'todo' }),
  });
  return res.ok ? res.json() : null;
}

// One to-do line. `crossing` = just checked (animating out).
export function TodoRow({ task, onToggle, crossing, showList, drag, completedLine }) {
  const done = task.status === 'done' || task.status === 'cancelled' || crossing;
  const people = task.assignees || [];
  return (
    <div className={'hq-todo-row' + (done ? ' is-done' : '') + (crossing ? ' is-crossing' : '') + (drag && drag.over ? ' is-drop-' + drag.over : '')}
      draggable={!!drag && !done}
      onDragStart={drag && drag.onDragStart} onDragEnd={drag && drag.onDragEnd}
      onDragOver={drag && drag.onDragOver} onDrop={drag && drag.onDrop}>
      <span className={'hq-todo-grip' + (drag && !done ? '' : ' is-off')} aria-hidden="true">⋮⋮</span>
      <CheckCircle checked={done} onClick={onToggle} />
      <div className="hq-todo-main">
        <Link to={`/tasks/${task.id}`} className="hq-todo-title">{task.title || '(untitled)'}</Link>
        <span className="hq-todo-meta">
          {task.status === 'in_progress' && <span className="hq-pill hq-tstatus-in_progress">In progress</span>}
          {task.status === 'cancelled' && <span className="hq-pill hq-tstatus-cancelled">Cancelled</span>}
          {task.priority && task.priority !== 'normal' && !done && <span className={`hq-pill hq-prio-${task.priority}`}>{task.priority}</span>}
          {people.length > 0 && (
            <span className="hq-todo-people">
              {people.slice(0, 4).map((p) => <Avatar key={p.id} person={p} />)}
              <span className="hq-todo-names">{people.map((p) => p.name.split(' ')[0]).join(', ')}</span>
            </span>
          )}
          {task.due_date && <span className={'hq-todo-due ' + dueTone(task.due_date, task.status)}>{fmtDue(task.due_date)}</span>}
          {task.recurrence && task.recurrence !== 'none' && <span className="hq-todo-tag" title="Repeats">↻</span>}
          {task.client_name && <span className="hq-todo-tag">{task.client_name}</span>}
          {showList && <span className="hq-todo-tag">{task.list_name || 'Inbox'}{task.group_name ? ' › ' + task.group_name : ''}</span>}
          {task.comment_count > 0 && <span className="hq-todo-tag" title="Comments">💬 {task.comment_count}</span>}
          {completedLine && task.completed_at && (
            <span className="hq-todo-by">
              {task.completed_by_name ? `${task.completed_by_name} · ` : ''}{fmtWhen(task.completed_at)}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// "Undo" toast after crossing something off.
export function useUndoToast() {
  const [toast, setToast] = useState(null);
  const show = (text, undo) => {
    const id = Date.now();
    setToast({ id, text, undo });
    setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), 6000);
  };
  const node = toast ? (
    <div className="hq-todo-toast" role="status">
      <span>{toast.text}</span>
      {toast.undo && <button type="button" onClick={() => { toast.undo(); setToast(null); }}>Undo</button>}
    </div>
  ) : null;
  return [node, show];
}
